import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Users, Upload, Download, Edit, Eye, AlertTriangle, CheckCircle, FileText, Send, StopCircle, Search, Clock, RefreshCw, Package, FileCheck, History, PlusCircle, ShieldCheck, UserPlus, AlertCircle, Check, FileUp, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AuditTrail from '@/components/shared/AuditTrail';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';

// Country codes for phone prefix
const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'USA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
];

// Check types with sub-options (same as PackagesTab)
const CHECK_TYPES_WITH_SUB_OPTIONS = {
  address_check: {
    name: 'Address Verification',
    subOptions: [
      { id: 'present', name: 'Present Address' },
      { id: 'permanent', name: 'Permanent Address' },
      { id: 'past_1', name: 'Past Address 1' },
      { id: 'past_2', name: 'Past Address 2' },
      { id: 'past_3', name: 'Past Address 3' },
      { id: 'past_4', name: 'Past Address 4' },
      { id: 'past_5', name: 'Past Address 5' }
    ]
  },
  criminal_court_check: {
    name: 'Criminal Court Check',
    subOptions: [
      { id: 'present', name: 'Present' },
      { id: 'permanent', name: 'Permanent' },
      { id: 'past_1', name: 'Past 1' },
      { id: 'past_2', name: 'Past 2' },
      { id: 'past_3', name: 'Past 3' },
      { id: 'past_4', name: 'Past 4' },
      { id: 'past_5', name: 'Past 5' }
    ]
  },
  criminal_advocate_check: {
    name: 'Criminal Advocate Check',
    subOptions: [
      { id: 'present', name: 'Present' },
      { id: 'permanent', name: 'Permanent' },
      { id: 'past_1', name: 'Past 1' },
      { id: 'past_2', name: 'Past 2' },
      { id: 'past_3', name: 'Past 3' },
      { id: 'past_4', name: 'Past 4' },
      { id: 'past_5', name: 'Past 5' }
    ]
  },
  id_check: {
    name: 'ID Verification',
    subOptions: [
      { id: 'pan', name: 'PAN' },
      { id: 'aadhar', name: 'Aadhar' },
      { id: 'driving_license', name: 'Driving License' },
      { id: 'passport', name: 'Passport' },
      { id: 'others', name: 'Others' }
    ]
  },
  employment_check: {
    name: 'Employment Verification',
    subOptions: [
      { id: 'current', name: 'Current Employment' },
      { id: 'past_1', name: 'Past Employment 1' },
      { id: 'past_2', name: 'Past Employment 2' },
      { id: 'past_3', name: 'Past Employment 3' },
      { id: 'past_4', name: 'Past Employment 4' },
      { id: 'past_5', name: 'Past Employment 5' }
    ]
  },
  education_check: {
    name: 'Education Verification',
    subOptions: [
      { id: 'highest', name: 'Highest Qualification' },
      { id: 'prior_ssc', name: 'Prior to Highest - SSC' },
      { id: 'prior_hsc', name: 'Prior to Highest - HSC' },
      { id: 'iti', name: 'ITI' },
      { id: 'diploma', name: 'Diploma' },
      { id: 'professional_course', name: 'Professional Course' }
    ]
  }
};

// Check type display names
const CHECK_DISPLAY_NAMES = {
  'address_check': 'Address Verification',
  'criminal_court_check': 'Criminal Court Check',
  'court_advocate_check': 'Court Advocate Check',
  'criminal_advocate_check': 'Criminal Advocate Check',
  'id_check': 'ID Verification',
  'employment_check': 'Employment Verification',
  'education_check': 'Education Verification',
  'credit_check': 'Credit Check',
  'drug_test': 'Drug Test',
  'reference_check': 'Reference Check',
  'personal_reference': 'Personal Reference',
  'professional_reference': 'Professional Reference',
  'global_database': 'Global Database Check',
  'world_criminal_db': 'World Criminal Database',
  'india_criminal_db': 'India Criminal Database'
};

// Helper function to get display name for check type
const getCheckDisplayName = (checkType) => {
  return CHECK_DISPLAY_NAMES[checkType] || CHECK_TYPES_WITH_SUB_OPTIONS[checkType]?.name || checkType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export default function EmployeesTab({ user } = {}) {
  const [employees, setEmployees] = useState([]);
  const [entities, setEntities] = useState([]);
  const [packages, setPackages] = useState([]);
  const [clientInfo, setClientInfo] = useState(null); // HR's client info with associated packages
  const [filteredPackages, setFilteredPackages] = useState([]); // Packages filtered by client association
  const [loading, setLoading] = useState(true);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedBulkEntity, setSelectedBulkEntity] = useState('');
  const [selectedBulkPackageId, setSelectedBulkPackageId] = useState('');
  const [showEditEmployee, setShowEditEmployee] = useState(null);
  const [showViewEmployee, setShowViewEmployee] = useState(null);
  const [employeeFullDetails, setEmployeeFullDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [editRequests, setEditRequests] = useState([]);
  const [basicEditRequests, setBasicEditRequests] = useState([]); // Employee-initiated basic info edit requests
  const [showBasicEditReview, setShowBasicEditReview] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [bulkData, setBulkData] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const fileInputRef = useRef(null);
  
  // Search and Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [inviteFilter, setInviteFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('recent'); // 'recent' or 'oldest'
  
  // Stop Check state
  const [showStopCheck, setShowStopCheck] = useState(null);
  const [stopCheckReason, setStopCheckReason] = useState('');
  const [stopCheckDescription, setStopCheckDescription] = useState('');
  const [stoppingCheck, setStoppingCheck] = useState(false);

  // Change Package state
  const [showChangePackage, setShowChangePackage] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [changingPackage, setChangingPackage] = useState(false);

  // Resume Upload and OCR state
  const [resumeFile, setResumeFile] = useState(null);
  const [extractingResume, setExtractingResume] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [showExtractedPreview, setShowExtractedPreview] = useState(false);

  const [newEmployee, setNewEmployee] = useState({
    entity_id: '',
    package_id: '',
    custom_checks: [], // For customizing checks when package allows
    name: '',
    father_name: '',
    dob: '',
    email: '',
    phone: '',
    phone_country_code: '+91', // Default India
    employee_code: '',
    employee_category: 'experienced', // fresher, experienced, intern, trainee
    present_house_no: '',
    present_area: '',
    present_city: '',
    present_state: '',
    present_pincode: '',
    permanent_house_no: '',
    permanent_area: '',
    permanent_city: '',
    permanent_state: '',
    permanent_pincode: '',
    same_as_present: false,
    invited: false,
    password: '', // Custom password for candidate login
    generate_password: true // Auto-generate password if not provided
  });

  // Employee category options
  const EMPLOYEE_CATEGORIES = [
    { value: 'experienced', label: 'Experienced', description: 'Has prior work experience' },
    { value: 'fresher', label: 'Fresher', description: 'Fresh graduate, no work experience' },
    { value: 'intern', label: 'Intern', description: 'Internship candidate' },
    { value: 'trainee', label: 'Trainee', description: 'Training program candidate' }
  ];

  const [editForm, setEditForm] = useState({});
  const [editReason, setEditReason] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [employeesRes, entitiesRes, packagesRes, editReqRes, basicEditReqRes, dashboardRes] = await Promise.all([
        axios.get('/employees'),
        axios.get('/entities'),
        axios.get('/packages'),
        axios.get('/edit-requests').catch(() => ({ data: [] })),
        axios.get('/basic-edit-requests').catch(() => ({ data: [] })),
        axios.get('/dashboard/stats').catch(() => ({ data: {} }))
      ]);
      setEmployees(employeesRes.data);
      setEntities(entitiesRes.data);
      if (entitiesRes.data.length > 0 && !selectedBulkEntity) {
        setSelectedBulkEntity(entitiesRes.data[0].id);
      }
      setPackages(packagesRes.data);
      setEditRequests(editReqRes.data || []);
      setBasicEditRequests(basicEditReqRes.data || []);
      
      // Get client info for filtering packages
      const clientId = dashboardRes.data?.client_id;
      if (clientId) {
        try {
          const clientRes = await axios.get(`/clients/${clientId}`);
          setClientInfo(clientRes.data);
          
          // Filter packages based on client's associated_packages
          const associatedPkgIds = clientRes.data.associated_packages || [];
          if (associatedPkgIds.length > 0) {
            const filtered = packagesRes.data.filter(pkg => associatedPkgIds.includes(pkg.id));
            setFilteredPackages(filtered);
          } else {
            // Also include packages assigned to client's entities
            const entityPkgIds = entitiesRes.data
              .map(e => e.package_id)
              .filter(Boolean);
            if (entityPkgIds.length > 0) {
              const entityPackages = packagesRes.data.filter(pkg => entityPkgIds.includes(pkg.id));
              setFilteredPackages(entityPackages);
            } else {
              // No packages associated - show empty (admin needs to configure)
              setFilteredPackages([]);
            }
          }
        } catch (e) {
          console.log('Could not fetch client info for package filtering');
          // Don't show all packages for client login - admin needs to configure
          setFilteredPackages([]);
        }
      } else {
        setFilteredPackages([]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewBasicEditRequest = async (requestId, action) => {
    try {
      await axios.put(`/basic-edit-requests/${requestId}/review`, {
        action,
        notes: reviewNotes
      });
      toast.success(`Edit request ${action}d successfully`);
      setShowBasicEditReview(null);
      setReviewNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} request`);
    }
  };

  // Handle Resume Upload for OCR extraction
  const handleResumeUpload = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExt)) {
      toast.error('Please upload a PDF or Word document (PDF, DOC, DOCX)');
      return;
    }
    
    setResumeFile(file);
    toast.success(`Resume "${file.name}" uploaded. Click "Extract with AI" to auto-fill form.`);
  };

  // Extract data from resume using AI OCR
  const handleExtractFromResume = async () => {
    if (!resumeFile) {
      toast.error('Please upload a resume first');
      return;
    }
    
    setExtractingResume(true);
    try {
      const formData = new FormData();
      formData.append('file', resumeFile);
      formData.append('document_type', 'resume');
      
      const response = await axios.post('/extract-document-data', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minute timeout for AI processing
      });
      
      if (response.data?.extracted_data) {
        const data = response.data.extracted_data;
        setExtractedData(data);
        setShowExtractedPreview(true);
        toast.success('Resume data extracted successfully! Review and confirm to auto-fill.');
      } else {
        toast.error('Could not extract data from resume. Please fill manually.');
      }
    } catch (error) {
      console.error('Resume extraction error:', error);
      toast.error(error.response?.data?.detail || 'Failed to extract data from resume');
    } finally {
      setExtractingResume(false);
    }
  };

  // Apply extracted data to form
  const applyExtractedData = () => {
    if (!extractedData) return;
    
    const data = extractedData;
    
    // Map extracted data to form fields
    const updatedEmployee = { ...newEmployee };
    
    // Basic info
    if (data.name) updatedEmployee.name = data.name;
    if (data.father_name) updatedEmployee.father_name = data.father_name;
    if (data.email) updatedEmployee.email = data.email;
    if (data.phone) {
      // Extract just the 10-digit number
      const phoneDigits = data.phone.replace(/\D/g, '').slice(-10);
      updatedEmployee.phone = phoneDigits;
    }
    if (data.date_of_birth || data.dob) {
      const dob = data.date_of_birth || data.dob;
      // Try to parse date format
      try {
        const parsed = new Date(dob);
        if (!isNaN(parsed)) {
          updatedEmployee.dob = parsed.toISOString().split('T')[0];
        }
      } catch (e) {
        // Keep original if parsing fails
      }
    }
    
    // Present Address
    if (data.present_address || data.address) {
      const addr = data.present_address || data.address;
      if (typeof addr === 'object') {
        if (addr.house_no || addr.street) updatedEmployee.present_house_no = addr.house_no || addr.street || '';
        if (addr.area || addr.locality) updatedEmployee.present_area = addr.area || addr.locality || '';
        if (addr.city) updatedEmployee.present_city = addr.city;
        if (addr.state) updatedEmployee.present_state = addr.state;
        if (addr.pincode || addr.zip) updatedEmployee.present_pincode = addr.pincode || addr.zip || '';
      } else if (typeof addr === 'string') {
        // Try to parse address string
        updatedEmployee.present_area = addr;
      }
    }
    
    // Permanent Address
    if (data.permanent_address) {
      const addr = data.permanent_address;
      if (typeof addr === 'object') {
        if (addr.house_no || addr.street) updatedEmployee.permanent_house_no = addr.house_no || addr.street || '';
        if (addr.area || addr.locality) updatedEmployee.permanent_area = addr.area || addr.locality || '';
        if (addr.city) updatedEmployee.permanent_city = addr.city;
        if (addr.state) updatedEmployee.permanent_state = addr.state;
        if (addr.pincode || addr.zip) updatedEmployee.permanent_pincode = addr.pincode || addr.zip || '';
      }
    }
    
    setNewEmployee(updatedEmployee);
    setShowExtractedPreview(false);
    toast.success('Form auto-filled from resume! Please review and complete any missing fields.');
  };

  // Resume dropzone configuration
  const { getRootProps: getResumeRootProps, getInputProps: getResumeInputProps, isDragActive: isResumeDragActive } = useDropzone({
    onDrop: handleResumeUpload,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const employeeData = { ...newEmployee };
      
      // Format phone with country code
      if (newEmployee.phone && newEmployee.phone_country_code) {
        employeeData.phone = formatPhoneWithCode(newEmployee.phone, newEmployee.phone_country_code);
      }
      
      if (newEmployee.same_as_present) {
        employeeData.permanent_house_no = newEmployee.present_house_no;
        employeeData.permanent_area = newEmployee.present_area;
        employeeData.permanent_city = newEmployee.present_city;
        employeeData.permanent_state = newEmployee.present_state;
        employeeData.permanent_pincode = newEmployee.present_pincode;
      }
      
      // Process custom checks - only include enabled checks
      if (employeeData.custom_checks?.length > 0) {
        employeeData.custom_checks = employeeData.custom_checks
          .filter(c => c.enabled)
          .map(c => ({
            check_type: c.check_type,
            quantity: c.quantity || 1,
            sub_checks: c.sub_checks || []
          }));
      }
      
      // Handle password - generate if not provided
      if (!employeeData.password && employeeData.generate_password) {
        // Auto-generate a simple password based on name and phone
        const namePart = (employeeData.name || 'user').split(' ')[0].toLowerCase();
        const phonePart = (employeeData.phone || '1234').slice(-4);
        employeeData.password = `${namePart}@${phonePart}`;
      }
      
      // Remove internal state fields
      delete employeeData.phone_country_code;
      delete employeeData.same_as_present;
      delete employeeData.generate_password;
      
      const response = await axios.post('/employees', employeeData);
      
      // Show password to user if it was auto-generated or custom set
      const createdPassword = employeeData.password || response.data?.password;
      if (createdPassword) {
        toast.success(
          <div>
            <p>Candidate added successfully!</p>
            <p className="text-xs mt-1">Login: <strong>{employeeData.email}</strong></p>
            <p className="text-xs">Password: <strong>{createdPassword}</strong></p>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.success('Candidate added successfully');
      }
      
      setShowAddEmployee(false);
      resetNewEmployee();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add candidate');
    }
  };

  const resetNewEmployee = () => {
    setNewEmployee({
      entity_id: '',
      package_id: '',
      custom_checks: [],
      name: '',
      father_name: '',
      dob: '',
      email: '',
      phone: '',
      phone_country_code: '+91', // Default India
      employee_code: '',
      present_house_no: '',
      present_area: '',
      present_city: '',
      present_state: '',
      present_pincode: '',
      permanent_house_no: '',
      permanent_area: '',
      permanent_city: '',
      permanent_state: '',
      permanent_pincode: '',
      same_as_present: false,
      invited: false
    });
  };

  const VALID_CATEGORIES = ['experienced', 'fresher', 'intern', 'trainee'];
  
  const handleBulkFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;

        // Column name aliases - map common variations to expected field names
        const columnAliases = {
          'alternate number': 'alternate_contact',
          'alternate_number': 'alternate_contact',
          'alt number': 'alternate_contact',
          'alt_number': 'alternate_contact',
          'alternate contact': 'alternate_contact',
          'alternate_phone': 'alternate_contact',
          'alt_contact': 'alternate_contact',
          'gender': 'gender',
          'sex': 'gender',
        };

        // Use Papaparse so multi-line quoted fields (e.g., a full address with
        // embedded newlines pasted from Excel) DO NOT get split into multiple rows.
        // Previously a hand-rolled `text.split('\n')` produced false 14-row counts
        // for a 2-row CSV when address cells contained line breaks.
        const parsed = Papa.parse(text, {
          header: true,
          skipEmptyLines: 'greedy', // drops blank-only lines including whitespace
          transformHeader: (h) => {
            const lower = String(h || '').toLowerCase().trim();
            return columnAliases[lower] || lower;
          },
        });

        const rawRows = parsed.data || [];
        const data = [];
        const errors = [];

        rawRows.forEach((rawRow, i) => {
          // Normalize values: trim and collapse internal newlines/whitespace so
          // an Excel-pasted multi-line address becomes a single clean string.
          const row = {};
          Object.keys(rawRow).forEach((k) => {
            const v = rawRow[k];
            if (typeof v === 'string') {
              row[k] = v.replace(/\r\n|\r|\n/g, ' ').replace(/\s+/g, ' ').trim();
            } else {
              row[k] = v == null ? '' : v;
            }
          });

          // Skip rows that are entirely empty
          if (!Object.values(row).some(v => v !== '' && v != null)) return;

          // Validate required fields
          const rowErrors = [];
          if (!row.name) rowErrors.push('Name is required');
          if (!row.email) rowErrors.push('Email is required');
          if (!row.phone) rowErrors.push('Phone is required');

          // Validate employee_category if provided
          if (row.employee_category) {
            const category = row.employee_category.toLowerCase().trim();
            if (!VALID_CATEGORIES.includes(category)) {
              rowErrors.push(`Invalid category "${row.employee_category}" - use: experienced, fresher, intern, or trainee`);
            } else {
              row.employee_category = category;
            }
          } else {
            row.employee_category = 'experienced';
          }

          // Format phone with default India country code
          if (row.phone) {
            row.phone = formatPhoneWithCode(row.phone, row.country_code || '+91');
          }

          // "Present address incomplete" is flagged ONLY when the client provided
          // ZERO address signal — any of house_no / area / city / state / pincode
          // counts as enough data for the client to import (per their request:
          // "with whatever information highlighted is filled... client should be
          // able to import").
          const insufficiencies = [];
          const hasAnyAddress = !!(
            row.present_house_no || row.present_area || row.present_city ||
            row.present_state || row.present_pincode
          );
          if (!hasAnyAddress) insufficiencies.push('Present address incomplete');

          data.push({
            ...row,
            row_number: i + 2, // +1 for header, +1 for 1-based display
            errors: rowErrors,
            insufficiencies,
            valid: rowErrors.length === 0
          });

          if (rowErrors.length > 0) {
            errors.push({ row: i + 2, errors: rowErrors });
          }
        });

        setBulkData(data);
        setBulkErrors(errors);
        toast.success(`Parsed ${data.length} record${data.length === 1 ? '' : 's'} from CSV`);
      } catch (err) {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  const [sendBulkEmails, setSendBulkEmails] = useState(true);
  
  const handleBulkImport = async () => {
    const validRecords = bulkData.filter(d => d.valid);
    if (validRecords.length === 0) {
      toast.error('No valid records to import');
      return;
    }

    try {
      const results = await axios.post('/employees/bulk-import', {
        send_invite_emails: sendBulkEmails,
        package_id: selectedBulkPackageId || undefined,
        employees: validRecords.map(r => ({
          name: r.name,
          email: r.email,
          phone: r.phone,
          employee_code: r.employee_code || '',
          employee_category: r.employee_category || 'experienced',
          father_name: r.father_name || '',
          dob: r.dob || '',
          present_house_no: r.present_house_no || '',
          present_area: r.present_area || '',
          present_city: r.present_city || '',
          present_state: r.present_state || '',
          present_pincode: r.present_pincode || '',
          permanent_house_no: r.permanent_house_no || '',
          permanent_area: r.permanent_area || '',
          permanent_city: r.permanent_city || '',
          permanent_state: r.permanent_state || '',
          permanent_pincode: r.permanent_pincode || '',
          insufficiencies: r.insufficiencies || [],
          entity_id: selectedBulkEntity || entities[0]?.id || ''
        }))
      });
      
      toast.success(
        <div data-testid="bulk-import-success-toast">
          <p>Successfully imported {results.data.imported} candidates</p>
          {sendBulkEmails && <p className="text-xs mt-1">Invitation emails sent with login credentials</p>}
        </div>,
        { duration: 8000 }
      );
      if (results.data.insufficiency_count > 0) {
        toast.info(`${results.data.insufficiency_count} candidates tagged with insufficiencies`);
      }
      const mirrorTotal = results.data.mirror_total_count || 0;
      if (mirrorTotal > 0) {
        const explicit = results.data.mirror_explicit_count || 0;
        const auto = results.data.mirror_auto_detected_count || 0;
        const breakdown = [];
        if (explicit) breakdown.push(`${explicit} via 'same_as_present' column`);
        if (auto) breakdown.push(`${auto} auto-detected (identical fields)`);
        toast.info(
          <div data-testid="bulk-import-mirror-toast">
            <p className="font-semibold">{mirrorTotal} permanent address{mirrorTotal === 1 ? '' : 'es'} mirrored from present</p>
            <p className="text-xs mt-1">{breakdown.join(' · ')}</p>
            <p className="text-xs mt-1 text-slate-600">These candidates will have their permanent address verification auto-synced from present — no separate vendor work.</p>
          </div>,
          { duration: 10000 }
        );
      }
      setShowBulkImport(false);
      setBulkData([]);
      setBulkErrors([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk import failed');
    }
  };

  const downloadTemplate = () => {
    // `same_as_present` (May 21, 2026): yes/y/true/1 → permanent address
    // mirrors present (auto-synced verification, no duplicate vendor work).
    // Blank → importer auto-detects when fields are identical.
    const headers = 'employee_code,employee_category,name,email,phone,gender,alternate_contact,father_name,dob,present_house_no,present_area,present_city,present_state,present_pincode,permanent_house_no,permanent_area,permanent_city,permanent_state,permanent_pincode,same_as_present';
    const sample1 = 'EMP001,experienced,John Doe,john@example.com,9876543210,Male,9123456789,Robert Doe,1995-01-15,123,Main Street,Mumbai,Maharashtra,400001,456,Park Avenue,Delhi,Delhi,110001,no';
    const sample2 = 'EMP002,experienced,Same Address,sa@example.com,9988776655,Female,9001122334,Father Name,1996-03-21,12 Lake Rd,HSR Layout,Bangalore,Karnataka,560102,,,,,,yes';
    const csv = `${headers}\n${sample1}\n${sample2}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const openEditDialog = (employee) => {
    setEditForm({ ...employee });
    setEditReason('');
    setShowEditEmployee(employee);
  };

  const handleSubmitEdit = async () => {
    if (!editReason) {
      toast.error('Please provide a reason for the edit');
      return;
    }

    try {
      await axios.post('/edit-requests', {
        employee_id: showEditEmployee.id,
        original_data: showEditEmployee,
        updated_data: editForm,
        reason: editReason,
        status: 'pending'
      });
      
      toast.success('Edit request submitted for admin approval');
      setShowEditEmployee(null);
      setEditForm({});
      setEditReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to submit edit request');
    }
  };

  const sendInvite = async (employeeId) => {
    try {
      await axios.post(`/employees/${employeeId}/send-invite`);
      toast.success('Invitation sent successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to send invitation');
    }
  };

  const handleStopCheck = async () => {
    if (!showStopCheck || !stopCheckReason) {
      toast.error('Please select a reason');
      return;
    }
    
    // Need to get case_id from employee
    const employee = showStopCheck;
    if (!employee.case_id) {
      toast.error('No active case found for this employee');
      return;
    }
    
    setStoppingCheck(true);
    try {
      const response = await axios.put(`/cases/${employee.case_id}/stop-check`, {
        reason: stopCheckReason,
        description: stopCheckDescription
      });
      toast.success(`Verification stopped - ${response.data.checks_stopped} checks stopped`);
      setShowStopCheck(null);
      setStopCheckReason('');
      setStopCheckDescription('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to stop check');
    } finally {
      setStoppingCheck(false);
    }
  };

  const canStopCheck = (employee) => {
    // Can stop check if employee has an active case and it's not already stopped/completed
    return employee.case_id && !employee.verification_stopped;
  };

  // Change Package handler
  const handleChangePackage = async () => {
    if (!showChangePackage || !selectedPackageId) {
      toast.error('Please select a package');
      return;
    }
    
    setChangingPackage(true);
    try {
      const response = await axios.put(`/employees/${showChangePackage.id}/change-package`, {
        package_id: selectedPackageId
      });
      toast.success(`Package changed successfully to ${response.data.new_package} (${response.data.checks_count} checks)`);
      setShowChangePackage(null);
      setSelectedPackageId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change package');
    } finally {
      setChangingPackage(false);
    }
  };

  const canChangePackage = (employee) => {
    // Can change package if employee hasn't submitted yet
    return employee.invited && !employee.declaration_signed;
  };

  const viewEmployeeDetails = async (employee) => {
    setShowViewEmployee(employee);
    setLoadingDetails(true);
    setActivityLog([]);
    try {
      const response = await axios.get(`/employees/${employee.id}/full-details`);
      setEmployeeFullDetails(response.data);
      
      // Fetch activity log if case exists
      if (response.data?.case?.id) {
        fetchActivityLog(response.data.case.id);
      }
    } catch (error) {
      console.error('Failed to load full details:', error);
      // Still show basic info even if full details fail
      setEmployeeFullDetails({ employee, documents: [], case: null, insufficiencies: [] });
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchActivityLog = async (caseId) => {
    setActivityLoading(true);
    try {
      const res = await axios.get(`/cases/${caseId}/activity-log`);
      setActivityLog(res.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
      setActivityLog([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const getActivityIcon = (iconName) => {
    const icons = {
      'plus-circle': <PlusCircle className="w-4 h-4" />,
      'check-circle': <CheckCircle className="w-4 h-4" />,
      'shield-check': <ShieldCheck className="w-4 h-4" />,
      'user-plus': <UserPlus className="w-4 h-4" />,
      'alert-triangle': <AlertTriangle className="w-4 h-4" />,
      'check': <Check className="w-4 h-4" />,
      'alert-circle': <AlertCircle className="w-4 h-4" />,
      'file-text': <FileText className="w-4 h-4" />,
      'check-square': <FileCheck className="w-4 h-4" />
    };
    return icons[iconName] || <Clock className="w-4 h-4" />;
  };

  const getActivityColor = (actionType) => {
    const colors = {
      'create': 'bg-blue-500',
      'submit': 'bg-emerald-500',
      'approve': 'bg-green-500',
      'assign': 'bg-indigo-500',
      'insufficiency': 'bg-amber-500',
      'clear': 'bg-teal-500',
      'complete': 'bg-emerald-600'
    };
    return colors[actionType] || 'bg-slate-500';
  };

  const generateReport = async (caseId, reportType) => {
    toast.info(`Generating ${reportType} report...`);
    try {
      const response = await axios.get(`/cases/${caseId}/report-data`);
      const data = response.data;
      const { generateVerificationReportPDF } = await import('@/utils/pdfExport');
      const fileName = await generateVerificationReportPDF({
        reportType,
        caseData: data.case,
        employeeData: data.employee,
        clientData: data.client,
        checksData: data.checks,
        annexures: data.annexures,
        fieldPhotos: data.field_photos,
        generatedBy: 'Client'
      });
      toast.success(`Report downloaded: ${fileName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    }
  };

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity ? entity.entity_name : '-';
  };

  const getStatusBadge = (employee) => {
    if (employee.verification_stopped) {
      return <Badge className="bg-red-100 text-red-700">Stopped</Badge>;
    }
    
    // First check explicit status field
    if (employee.status === 'invite_sent') {
      return <Badge className="bg-blue-100 text-blue-700">Invite Sent</Badge>;
    }
    
    // Check for insufficiencies - only use backend flag (set by operations team)
    if (employee.has_insufficiency) {
      return <Badge className="bg-rose-100 text-rose-700"><AlertTriangle className="w-3 h-3 mr-1" />Insufficient</Badge>;
    } else if (employee.declaration_signed) {
      return <Badge className="bg-emerald-100 text-emerald-700">Submitted</Badge>;
    } else if (employee.details_filled) {
      return <Badge className="bg-amber-100 text-amber-700">Details Filled</Badge>;
    } else if (employee.invited) {
      return <Badge className="bg-blue-100 text-blue-700">Submission Pending</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">Created</Badge>;
  };

  // Check for missing required data - package-aware
  const checkForMissingData = (employee) => {
    // Check personal required fields
    if (!employee?.pan_number) return true;
    if (!employee?.aadhar_number) return true;
    
    // Check present address
    if (!employee?.present_city && !employee?.present_address?.city) return true;
    if (!employee?.present_pincode && !employee?.present_address?.pincode) return true;
    
    // Check permanent address (unless same as present)
    if (!employee?.permanent_same_as_present) {
      if (!employee?.permanent_city && !employee?.permanent_address?.city) return true;
    }
    
    // Determine which check types the client's packages require
    const allPackageChecks = filteredPackages.flatMap(pkg => 
      (pkg.checks || []).map(c => typeof c === 'string' ? c : c.check_type)
    );
    const requiresEducation = allPackageChecks.some(ct => ct === 'education_check');
    const requiresEmployment = allPackageChecks.some(ct => ct === 'employment_check');
    
    // Check education only if client package requires it
    if (requiresEducation) {
      if (!employee?.education_entries || employee.education_entries.length === 0) return true;
    }
    
    // Check employment only if client package requires it (unless fresher)
    if (requiresEmployment) {
      if (!employee?.is_fresher && (!employee?.employment_entries || employee.employment_entries.length === 0)) return true;
    }
    
    return false;
  };

  // Send reminder (same as invite but for already invited employees)
  const sendReminder = async (employeeId) => {
    try {
      await axios.post(`/employees/${employeeId}/send-reminder`);
      toast.success('Reminder sent successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  // Helper to format phone with country code
  const formatPhoneWithCode = (phone, countryCode = '+91') => {
    if (!phone) return '';
    // Remove any existing country code
    const cleanPhone = phone.replace(/^\+\d{1,3}/, '').replace(/^0+/, '');
    return `${countryCode}${cleanPhone}`;
  };

  // Export the currently-filtered candidate list to an Excel-compatible CSV.
  // Includes the columns the HR/Client team requested: identity, contact, key
  // dates and verification outcome. Uses the same `getFilteredEmployees()`
  // result so the export honors active search + status filter.
  const exportCandidatesToExcel = () => {
    const rows = getFilteredEmployees();
    if (!rows || rows.length === 0) {
      toast.error('No candidates to export with the current filters');
      return;
    }
    const fmt = (val) => {
      if (!val) return '';
      try {
        const d = new Date(val);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch { return ''; }
    };
    const overallStatusLabel = (e) => {
      if (e.verification_stopped) return 'Stopped';
      if (e.case_status === 'completed' || e.case_status === 'final_report_qc' || e.case_status === 'report_approved') return 'Verification Complete';
      if (e.case_status === 'in_progress' || e.case_status === 'qc_approved' || e.case_status === 'basic_initiated') return 'In Progress';
      if (e.has_insufficiency) return 'Insufficiency';
      if (e.declaration_signed) return 'Submitted - Pending QC';
      if (e.details_filled) return 'Details Filled';
      if (e.invited) return 'Invited - Pending';
      return 'Created';
    };
    const headers = [
      'Date Added', 'Date Submitted', 'Date Case Completed',
      'Case ID', 'Emp Code', 'Entity Name', 'Name', 'Father Name', 'DOB',
      'Email ID', 'Contact Number',
      'Overall Verification Status', 'Verification Result',
    ];
    const escape = (v) => {
      const s = (v ?? '').toString();
      // Escape quotes per RFC 4180 and wrap in quotes when needed.
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [headers.join(',')];
    rows.forEach(e => {
      lines.push([
        fmt(e.created_at),
        fmt(e.case_submitted_at),
        fmt(e.case_completed_at),
        e.case_id || '',
        e.employee_code || '',
        getEntityName(e.entity_id),
        e.name || '',
        e.father_name || '',
        e.dob || '',
        e.email || '',
        e.phone || '',
        overallStatusLabel(e),
        (e.overall_result || '').toString().replace(/_/g, ' '),
      ].map(escape).join(','));
    });
    // BOM so Excel recognizes UTF-8 (preserves Indian names with diacritics).
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `candidates_export_${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} candidate(s) to CSV`);
  };

  // Filter employees based on search and filters
  const getFilteredEmployees = () => {
    let filtered = employees;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        e.name?.toLowerCase().includes(query) ||
        e.email?.toLowerCase().includes(query) ||
        e.phone?.includes(query) ||
        e.employee_code?.toLowerCase().includes(query)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(e => {
        switch (statusFilter) {
          case 'insufficient':
            return e.has_insufficiency;
          case 'submitted':
            return e.declaration_signed;
          case 'details_filled':
            return e.details_filled && !e.declaration_signed;
          case 'pending':
            return e.invited && !e.details_filled;
          case 'stopped':
            return e.verification_stopped;
          case 'created':
            return !e.invited && e.status !== 'invite_sent';
          case 'invite_sent':
            return e.status === 'invite_sent' || (e.invited && !e.details_filled);
          case 'verification_complete':
            return e.case_status === 'completed' || e.case_status === 'final_report_qc' || e.case_status === 'report_approved';
          default:
            return true;
        }
      });
    }
    
    // Invite filter
    if (inviteFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(e => {
        if (!e.invitation_sent_at) return inviteFilter === 'not_sent';
        
        const sentDate = new Date(e.invitation_sent_at);
        const daysDiff = Math.floor((now - sentDate) / (1000 * 60 * 60 * 24));
        
        switch (inviteFilter) {
          case 'sent':
            return true;
          case 'not_sent':
            return !e.invited;
          case 'recent_24h':
            return daysDiff < 1;
          case 'recent_week':
            return daysDiff < 7;
          case 'older':
            return daysDiff >= 7;
          default:
            return true;
        }
      });
    }
    
    // Date filter (created date)
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(e => {
        if (!e.created_at) return false;
        const createdDate = new Date(e.created_at);
        const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
        
        switch (dateFilter) {
          case 'today':
            return createdDate >= today;
          case 'yesterday':
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            return createdDate >= yesterday && createdDate < today;
          case 'last_7_days':
            return daysDiff < 7;
          case 'last_30_days':
            return daysDiff < 30;
          case 'last_90_days':
            return daysDiff < 90;
          default:
            return true;
        }
      });
    }
    
    // Sort by created date
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return sortOrder === 'recent' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  };

  const hasPendingEdit = (employeeId) => {
    return editRequests.some(r => r.employee_id === employeeId && r.status === 'pending');
  };

  // Calculate employee status stats
  const getEmployeeStats = () => {
    const invitesSent = employees.filter(e => e.status === 'invite_sent' || e.invited).length;
    const pendingSubmission = employees.filter(e => e.invited && !e.details_filled).length;
    const inProcess = employees.filter(e => e.details_filled && !e.declaration_signed).length;
    // Only count as insufficient if backend has flagged it
    const insufficient = employees.filter(e => e.has_insufficiency).length;
    const submitted = employees.filter(e => e.declaration_signed).length;
    // Verification Complete - employees whose cases have reached final status
    const verificationComplete = employees.filter(e => 
      e.case_status === 'completed' || 
      e.case_status === 'final_report_qc' || 
      e.case_status === 'report_approved'
    ).length;
    
    return { invitesSent, pendingSubmission, inProcess, insufficient, submitted, verificationComplete };
  };

  const employeeStats = getEmployeeStats();

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Candidates</h1>
          <p className="text-slate-600">Manage candidate verification requests</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCandidatesToExcel} data-testid="export-candidates-btn">
            <Download className="w-4 h-4 mr-2" />
            Export to Excel
          </Button>
          <Button variant="outline" onClick={() => setShowBulkImport(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setShowAddEmployee(true)} data-testid="add-employee-button">
            <Plus className="w-4 h-4 mr-2" />
            Add Candidate
          </Button>
        </div>
      </div>

      {/* Employee Status Dashboard */}
      <Card className="mb-4 border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4" />
            Employee Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <div 
              className="flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors min-w-[140px]"
              onClick={() => setInviteFilter('sent')}
              data-testid="status-invites-sent"
            >
              <Send className="w-4 h-4 text-blue-600" />
              <div>
                <p className="text-xs text-blue-600">Invites Sent</p>
                <p className="text-lg font-bold text-blue-700">{employeeStats.invitesSent}</p>
              </div>
            </div>
            <div 
              className="flex items-center gap-3 px-4 py-2 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors min-w-[140px]"
              onClick={() => setStatusFilter('pending')}
              data-testid="status-pending-submission"
            >
              <Clock className="w-4 h-4 text-amber-600" />
              <div>
                <p className="text-xs text-amber-600">Pending Submission</p>
                <p className="text-lg font-bold text-amber-700">{employeeStats.pendingSubmission}</p>
              </div>
            </div>
            <div 
              className="flex items-center gap-3 px-4 py-2 bg-indigo-50 rounded-lg cursor-pointer hover:bg-indigo-100 transition-colors min-w-[140px]"
              onClick={() => setStatusFilter('details_filled')}
              data-testid="status-in-process"
            >
              <RefreshCw className="w-4 h-4 text-indigo-600" />
              <div>
                <p className="text-xs text-indigo-600">In Progress</p>
                <p className="text-lg font-bold text-indigo-700">{employeeStats.inProcess}</p>
              </div>
            </div>
            <div 
              className="flex items-center gap-3 px-4 py-2 bg-rose-50 rounded-lg cursor-pointer hover:bg-rose-100 transition-colors min-w-[140px]"
              onClick={() => setStatusFilter('insufficient')}
              data-testid="status-insufficient"
            >
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <div>
                <p className="text-xs text-rose-600">Insufficient</p>
                <p className="text-lg font-bold text-rose-700">{employeeStats.insufficient}</p>
              </div>
            </div>
            <div 
              className="flex items-center gap-3 px-4 py-2 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors min-w-[140px]"
              onClick={() => setStatusFilter('submitted')}
              data-testid="status-submitted"
            >
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <div>
                <p className="text-xs text-emerald-600">Submitted</p>
                <p className="text-lg font-bold text-emerald-700">{employeeStats.submitted}</p>
              </div>
            </div>
            <div 
              className="flex items-center gap-3 px-4 py-2 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors min-w-[140px]"
              onClick={() => setStatusFilter('verification_complete')}
              data-testid="status-verification-complete"
            >
              <FileCheck className="w-4 h-4 text-green-600" />
              <div>
                <p className="text-xs text-green-600">Verification Complete</p>
                <p className="text-lg font-bold text-green-700">{employeeStats.verificationComplete}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500">Search</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input 
                  placeholder="Search by name, email, phone, ID..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            
            {/* Status Filter */}
            <div className="w-[180px]">
              <Label className="text-xs text-slate-500">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="created">Created</SelectItem>
                  <SelectItem value="pending">Submission Pending</SelectItem>
                  <SelectItem value="details_filled">Details Filled</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="insufficient">Insufficient</SelectItem>
                  <SelectItem value="verification_complete">Verification Complete</SelectItem>
                  <SelectItem value="stopped">Stopped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Date Filter */}
            <div className="w-[180px]">
              <Label className="text-xs text-slate-500">Created Date</Label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger data-testid="date-filter">
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                  <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                  <SelectItem value="last_90_days">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Sort Order */}
            <div className="w-[150px]">
              <Label className="text-xs text-slate-500">Sort By</Label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger data-testid="sort-order">
                  <SelectValue placeholder="Recent First" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Recent First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Invite Filter */}
            <div className="w-[180px]">
              <Label className="text-xs text-slate-500">Invite Status</Label>
              <Select value={inviteFilter} onValueChange={setInviteFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Invites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="not_sent">Not Sent</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="recent_24h">Sent (Last 24h)</SelectItem>
                  <SelectItem value="recent_week">Sent (Last Week)</SelectItem>
                  <SelectItem value="older">Sent (Older)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Clear Filters */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => { setSearchQuery(''); setStatusFilter('all'); setInviteFilter('all'); setDateFilter('all'); setSortOrder('recent'); }}
              className="h-10"
              data-testid="clear-filters"
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pending Edit Requests Alert */}
      {editRequests.filter(r => r.status === 'pending').length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">{editRequests.filter(r => r.status === 'pending').length} edit request(s) pending admin approval</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Basic Info Edit Requests */}
      {basicEditRequests.filter(r => r.status === 'pending').length > 0 && (
        <Card className="mb-6 border-blue-200 bg-blue-50" data-testid="basic-edit-requests-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-blue-800 text-lg">
              <Edit className="w-5 h-5" />
              Basic Info Edit Requests ({basicEditRequests.filter(r => r.status === 'pending').length} pending)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {basicEditRequests.filter(r => r.status === 'pending').map((request) => (
                <div 
                  key={request.id} 
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200"
                  data-testid={`basic-edit-request-${request.id}`}
                >
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">{request.employee_name}</p>
                    <p className="text-sm text-slate-600">
                      Wants to edit: <span className="font-medium text-blue-700">{request.fields_to_edit?.join(', ')}</span>
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Reason: {request.reason}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Requested: {new Date(request.requested_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-green-400 text-green-700 hover:bg-green-50"
                      onClick={() => setShowBasicEditReview(request)}
                      data-testid={`approve-basic-edit-${request.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Review
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {getFilteredEmployees().length > 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-500">
                Showing {getFilteredEmployees().length} of {employees.length} candidates
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Emp ID/Offer ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invite Sent</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredEmployees().map((employee) => (
                  <TableRow key={employee.id} data-testid={`employee-row-${employee.id}`}>
                    <TableCell className="font-mono text-xs">{employee.employee_code || '-'}</TableCell>
                    <TableCell className="font-medium">{employee.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        employee.employee_category === 'fresher' ? 'bg-green-50 text-green-700 border-green-300' :
                        employee.employee_category === 'intern' ? 'bg-purple-50 text-purple-700 border-purple-300' :
                        employee.employee_category === 'trainee' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                        'bg-blue-50 text-blue-700 border-blue-300'
                      }>
                        {(employee.employee_category || 'experienced').charAt(0).toUpperCase() + (employee.employee_category || 'experienced').slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>{employee.email}</TableCell>
                    <TableCell>{employee.phone}</TableCell>
                    <TableCell>{getEntityName(employee.entity_id)}</TableCell>
                    <TableCell>{getStatusBadge(employee)}</TableCell>
                    <TableCell>
                      {employee.invitation_sent_at ? (
                        <div className="text-xs">
                          <span className="text-emerald-600">✓ Sent</span>
                          <br />
                          <span className="text-slate-400">{new Date(employee.invitation_sent_at).toLocaleDateString()}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>{new Date(employee.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => viewEmployeeDetails(employee)} title="View Details" data-testid={`view-employee-${employee.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDialog(employee)} 
                          title="Edit"
                          disabled={hasPendingEdit(employee.id)}
                          className={hasPendingEdit(employee.id) ? 'opacity-50' : ''}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {!employee.invited ? (
                          <Button variant="ghost" size="sm" onClick={() => sendInvite(employee.id)} title="Send Invite" className="text-blue-600">
                            <Send className="w-4 h-4" />
                          </Button>
                        ) : !employee.declaration_signed && (
                          <Button variant="ghost" size="sm" onClick={() => sendReminder(employee.id)} title="Send Reminder" className="text-amber-600">
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        {canChangePackage(employee) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setShowChangePackage(employee);
                              setSelectedPackageId('');
                            }} 
                            title="Change Package"
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            data-testid={`change-package-${employee.id}`}
                          >
                            <Package className="w-4 h-4" />
                          </Button>
                        )}
                        {canStopCheck(employee) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setShowStopCheck(employee)} 
                            title="Stop Check"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`stop-check-${employee.id}`}
                          >
                            <StopCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : employees.length > 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="text-center py-12">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No candidates match your filters</p>
            <Button 
              variant="outline" 
              onClick={() => { setSearchQuery(''); setStatusFilter('all'); setInviteFilter('all'); }}
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No candidates added yet</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={() => setShowBulkImport(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Bulk Import
              </Button>
              <Button onClick={() => setShowAddEmployee(true)} className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Add Candidate
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Candidate Dialog */}
      <Dialog open={showAddEmployee} onOpenChange={setShowAddEmployee}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Candidate</DialogTitle>
          </DialogHeader>
          
          {/* Resume Upload Section */}
          <div className="mb-4 p-4 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-lg border border-violet-200">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-violet-600" />
              <h3 className="font-semibold text-violet-900">Auto-fill from Resume (AI-Powered)</h3>
            </div>
            <div className="flex items-center gap-4">
              <div 
                {...getResumeRootProps()} 
                className={`flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                  isResumeDragActive 
                    ? 'border-violet-500 bg-violet-100' 
                    : resumeFile 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-gray-300 hover:border-violet-400 hover:bg-violet-50'
                }`}
              >
                <input {...getResumeInputProps()} />
                {resumeFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <FileText className="h-5 w-5" />
                    <span className="font-medium">{resumeFile.name}</span>
                    <CheckCircle className="h-4 w-4" />
                  </div>
                ) : (
                  <div className="text-gray-600">
                    <FileUp className="h-6 w-6 mx-auto mb-1" />
                    <p className="text-sm">Drop resume here or click to upload</p>
                    <p className="text-xs text-gray-400">PDF, DOC, DOCX (max 10MB)</p>
                  </div>
                )}
              </div>
              <Button 
                type="button"
                onClick={handleExtractFromResume}
                disabled={!resumeFile || extractingResume}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {extractingResume ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Extract with AI
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <form onSubmit={handleAddEmployee}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="present">Present Address</TabsTrigger>
                <TabsTrigger value="permanent">Permanent Address</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Entity *</Label>
                    <Select 
                      value={newEmployee.entity_id} 
                      onValueChange={(v) => {
                        // When entity changes, auto-set the package from that entity
                        const selectedEntity = entities.find(e => e.id === v);
                        const entityPackageId = selectedEntity?.package_id || '';
                        const selectedPkg = packages.find(p => p.id === entityPackageId);
                        const initialChecks = selectedPkg?.checks?.map(c => ({
                          ...c,
                          enabled: true,
                          quantity: c.quantity || 1
                        })) || [];
                        setNewEmployee({
                          ...newEmployee, 
                          entity_id: v, 
                          package_id: entityPackageId,
                          custom_checks: initialChecks
                        });
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                      <SelectContent>
                        {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Package *</Label>
                    {(() => {
                      // Get the package associated with the selected entity
                      const selectedEntity = entities.find(e => e.id === newEmployee.entity_id);
                      const entityPackageId = selectedEntity?.package_id;
                      const entityPackage = packages.find(p => p.id === entityPackageId);
                      
                      // If entity has a specific package, show only that package
                      // Otherwise fall back to client's associated packages only (not all)
                      const availablePackages = entityPackage 
                        ? [entityPackage]
                        : filteredPackages;
                      
                      return (
                        <Select 
                          value={newEmployee.package_id} 
                          onValueChange={(v) => {
                            // When package changes, initialize custom_checks from package
                            const selectedPkg = packages.find(p => p.id === v);
                            const initialChecks = selectedPkg?.checks?.map(c => ({
                              ...c,
                              enabled: true,
                              quantity: c.quantity || 1
                            })) || [];
                            setNewEmployee({...newEmployee, package_id: v, custom_checks: initialChecks});
                          }}
                        >
                          <SelectTrigger><SelectValue placeholder="Select package" /></SelectTrigger>
                          <SelectContent>
                            {availablePackages.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    })()}
                    {newEmployee.entity_id && (
                      <p className="text-xs text-blue-600 mt-1">
                        {entities.find(e => e.id === newEmployee.entity_id)?.package_id 
                          ? 'Showing package assigned to selected entity'
                          : 'Select an entity to see its assigned package'}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Package Check Customization - Shows when package is selected */}
                {newEmployee.package_id && newEmployee.custom_checks?.length > 0 && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-slate-700">Customize Verification Checks</Label>
                      <span className="text-xs text-slate-500">
                        {newEmployee.custom_checks.filter(c => c.enabled).reduce((sum, c) => sum + (c.quantity || 1), 0)} checks selected
                      </span>
                    </div>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {newEmployee.custom_checks.map((check, idx) => {
                        const checkTypeInfo = CHECK_TYPES_WITH_SUB_OPTIONS[check.check_type];
                        const hasSubOptions = !!checkTypeInfo?.subOptions;
                        const selectedSubOptions = check.selectedSubOptions || [];
                        
                        return (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-lg border ${
                              check.enabled ? 'bg-white border-blue-200' : 'bg-slate-100 border-slate-200'
                            }`}
                          >
                            {/* Main Check Row */}
                            <div className="flex items-center justify-between">
                              <label className="flex items-center gap-3 cursor-pointer flex-1">
                                <input
                                  type="checkbox"
                                  checked={check.enabled}
                                  onChange={(e) => {
                                    const updatedChecks = [...newEmployee.custom_checks];
                                    updatedChecks[idx].enabled = e.target.checked;
                                    // Reset sub-options when disabling
                                    if (!e.target.checked) {
                                      updatedChecks[idx].selectedSubOptions = [];
                                      updatedChecks[idx].quantity = 1;
                                    }
                                    setNewEmployee({...newEmployee, custom_checks: updatedChecks});
                                  }}
                                  className="rounded border-slate-300"
                                />
                                <span className={`font-medium ${check.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
                                  {checkTypeInfo?.name || check.check_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              </label>
                              {check.enabled && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500">Qty:</span>
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 font-semibold">
                                    {check.quantity || 1}
                                  </Badge>
                                </div>
                              )}
                            </div>
                            
                            {/* Sub-Options Row - Show when check is enabled and has sub-options */}
                            {check.enabled && hasSubOptions && (
                              <div className="mt-3 ml-8 pl-4 border-l-2 border-blue-200">
                                <p className="text-xs text-slate-500 mb-2">Select options (quantity auto-calculated):</p>
                                <div className="flex flex-wrap gap-2">
                                  {checkTypeInfo.subOptions.map((subOption) => {
                                    const isSelected = selectedSubOptions.includes(subOption.id);
                                    return (
                                      <label 
                                        key={subOption.id}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-colors text-sm ${
                                          isSelected
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white border border-slate-300 text-slate-700 hover:border-blue-400'
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(e) => {
                                            const updatedChecks = [...newEmployee.custom_checks];
                                            const currentSubOptions = updatedChecks[idx].selectedSubOptions || [];
                                            
                                            let newSubOptions;
                                            if (e.target.checked) {
                                              newSubOptions = [...currentSubOptions, subOption.id];
                                            } else {
                                              newSubOptions = currentSubOptions.filter(id => id !== subOption.id);
                                            }
                                            
                                            updatedChecks[idx].selectedSubOptions = newSubOptions;
                                            // Auto-calculate quantity based on selected sub-options
                                            updatedChecks[idx].quantity = newSubOptions.length > 0 ? newSubOptions.length : 1;
                                            
                                            setNewEmployee({...newEmployee, custom_checks: updatedChecks});
                                          }}
                                          className="sr-only"
                                        />
                                        {subOption.name}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {/* Manual Qty for checks without sub-options */}
                            {check.enabled && !hasSubOptions && (
                              <div className="mt-2 ml-8 flex items-center gap-2">
                                <span className="text-xs text-slate-500">Quantity:</span>
                                <select
                                  value={check.quantity || 1}
                                  onChange={(e) => {
                                    const updatedChecks = [...newEmployee.custom_checks];
                                    updatedChecks[idx].quantity = parseInt(e.target.value);
                                    setNewEmployee({...newEmployee, custom_checks: updatedChecks});
                                  }}
                                  className="w-16 text-sm border rounded px-2 py-1"
                                >
                                  {[1, 2, 3, 4, 5].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-500 mt-3">
                      Select sub-options for each check type. Quantity is auto-calculated based on selected options.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <Input value={newEmployee.name} onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Father's Name *</Label>
                    <Input value={newEmployee.father_name} onChange={(e) => setNewEmployee({...newEmployee, father_name: e.target.value})} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Employee Category *</Label>
                    <Select 
                      value={newEmployee.employee_category} 
                      onValueChange={(v) => setNewEmployee({...newEmployee, employee_category: v})}
                    >
                      <SelectTrigger className={newEmployee.employee_category !== 'experienced' ? 'border-blue-400 bg-blue-50' : ''}>
                        <SelectValue placeholder="Select Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYEE_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            <div className="flex flex-col">
                              <span className="font-medium">{cat.label}</span>
                              <span className="text-xs text-slate-500">{cat.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newEmployee.employee_category !== 'experienced' && (
                      <p className="text-xs text-blue-600 mt-1">Employment verification will be skipped for {newEmployee.employee_category}</p>
                    )}
                  </div>
                  <div>
                    <Label>Employee ID / Offer ID</Label>
                    <Input 
                      value={newEmployee.employee_code} 
                      onChange={(e) => setNewEmployee({...newEmployee, employee_code: e.target.value})} 
                      placeholder="e.g., EMP001 or OFFER-2026-001"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date of Birth *</Label>
                    <Input type="date" value={newEmployee.dob} onChange={(e) => setNewEmployee({...newEmployee, dob: e.target.value})} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={newEmployee.email} onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <div className="flex gap-2">
                      <Select 
                        value={newEmployee.phone_country_code} 
                        onValueChange={(v) => setNewEmployee({...newEmployee, phone_country_code: v})}
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRY_CODES.map(cc => (
                            <SelectItem key={cc.code} value={cc.code}>
                              {cc.flag} {cc.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        value={newEmployee.phone} 
                        onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value.replace(/\D/g, '')})} 
                        placeholder="9876543210"
                        className="flex-1"
                        required 
                      />
                    </div>
                  </div>
                </div>
                
                {/* Password Section */}
                <div className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">Candidate Login Password</Label>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="generate_password"
                        checked={newEmployee.generate_password}
                        onCheckedChange={(checked) => setNewEmployee({...newEmployee, generate_password: checked, password: ''})}
                      />
                      <label htmlFor="generate_password" className="text-xs text-slate-600 cursor-pointer">Auto-generate</label>
                    </div>
                  </div>
                  {!newEmployee.generate_password && (
                    <Input 
                      type="text"
                      value={newEmployee.password} 
                      onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})} 
                      placeholder="Enter custom password"
                      className="bg-white"
                    />
                  )}
                  <p className="text-xs text-slate-500 mt-2">
                    {newEmployee.generate_password 
                      ? "Password will be auto-generated as: [firstname]@[last4digits of phone]" 
                      : "Set a custom password for candidate login"}
                  </p>
                </div>
              </TabsContent>
              
              <TabsContent value="present" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>House No/Building</Label>
                    <Input value={newEmployee.present_house_no} onChange={(e) => setNewEmployee({...newEmployee, present_house_no: e.target.value})} />
                  </div>
                  <div>
                    <Label>Area/Locality</Label>
                    <Input value={newEmployee.present_area} onChange={(e) => setNewEmployee({...newEmployee, present_area: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input value={newEmployee.present_city} onChange={(e) => setNewEmployee({...newEmployee, present_city: e.target.value})} />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input value={newEmployee.present_state} onChange={(e) => setNewEmployee({...newEmployee, present_state: e.target.value})} />
                  </div>
                  <div>
                    <Label>Pincode</Label>
                    <Input value={newEmployee.present_pincode} onChange={(e) => setNewEmployee({...newEmployee, present_pincode: e.target.value})} />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="permanent" className="space-y-4 mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="same_address"
                    checked={newEmployee.same_as_present}
                    onChange={(e) => setNewEmployee({...newEmployee, same_as_present: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="same_address" className="cursor-pointer">Same as Present Address</Label>
                </div>
                {!newEmployee.same_as_present && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>House No/Building</Label>
                        <Input value={newEmployee.permanent_house_no} onChange={(e) => setNewEmployee({...newEmployee, permanent_house_no: e.target.value})} />
                      </div>
                      <div>
                        <Label>Area/Locality</Label>
                        <Input value={newEmployee.permanent_area} onChange={(e) => setNewEmployee({...newEmployee, permanent_area: e.target.value})} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>City</Label>
                        <Input value={newEmployee.permanent_city} onChange={(e) => setNewEmployee({...newEmployee, permanent_city: e.target.value})} />
                      </div>
                      <div>
                        <Label>State</Label>
                        <Input value={newEmployee.permanent_state} onChange={(e) => setNewEmployee({...newEmployee, permanent_state: e.target.value})} />
                      </div>
                      <div>
                        <Label>Pincode</Label>
                        <Input value={newEmployee.permanent_pincode} onChange={(e) => setNewEmployee({...newEmployee, permanent_pincode: e.target.value})} />
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
            
            <div className="flex items-center gap-2 mt-6 pt-4 border-t">
              <input
                type="checkbox"
                id="send_invite"
                checked={newEmployee.invited}
                onChange={(e) => setNewEmployee({...newEmployee, invited: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="send_invite" className="cursor-pointer">Send invitation email to candidate</Label>
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddEmployee(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add Candidate</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Import Candidates</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h3 className="font-medium mb-2">Instructions:</h3>
              <ul className="text-sm text-slate-600 space-y-1 list-disc ml-4">
                <li>Download the CSV template and fill in candidate details</li>
                <li>Required fields: Name, Email, Phone</li>
                <li>Optional fields: <strong>Employee ID/Offer ID</strong>, <strong>Employee Category</strong>, Father's Name, DOB, Address details</li>
                <li><strong>Employee Category</strong> options: <code>experienced</code> (default), <code>fresher</code>, <code>intern</code>, <code>trainee</code></li>
                <li>Resume upload is not required during bulk import</li>
              </ul>
              <Button variant="outline" className="mt-3" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
            
            <div>
              {entities.length > 1 && (
                <div className="mb-4">
                  <Label>Select Entity</Label>
                  <select
                    data-testid="bulk-import-entity-select"
                    className="w-full mt-1 p-2 border rounded-md text-sm"
                    value={selectedBulkEntity}
                    onChange={(e) => {
                      setSelectedBulkEntity(e.target.value);
                      const ent = entities.find(en => en.id === e.target.value);
                      setSelectedBulkPackageId(ent?.package_id || '');
                    }}
                  >
                    {entities.map(e => (
                      <option key={e.id} value={e.id}>{e.entity_name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mb-4">
                <Label>Select Package</Label>
                <select
                  data-testid="bulk-import-package-select"
                  className="w-full mt-1 p-2 border rounded-md text-sm"
                  value={selectedBulkPackageId}
                  onChange={(e) => setSelectedBulkPackageId(e.target.value)}
                >
                  <option value="">-- Use entity default --</option>
                  {(filteredPackages.length > 0 ? filteredPackages : packages).map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({(p.checks || []).length} checks)</option>
                  ))}
                </select>
              </div>
              <Label>Upload CSV File</Label>
              <div className="mt-2 flex gap-2">
                <Input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleBulkFileUpload}
                  className="flex-1"
                />
              </div>
            </div>
            
            {bulkData.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">Preview ({bulkData.length} records)</h3>
                  <div className="flex gap-2">
                    <Badge className="bg-emerald-100 text-emerald-700">{bulkData.filter(d => d.valid).length} Valid</Badge>
                    <Badge className="bg-rose-100 text-rose-700">{bulkData.filter(d => !d.valid).length} Errors</Badge>
                    <Badge className="bg-amber-100 text-amber-700">{bulkData.filter(d => d.insufficiencies?.length > 0).length} With Insufficiency</Badge>
                  </div>
                </div>
                
                <div className="max-h-64 overflow-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>Emp ID</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Issues</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bulkData.map((row, idx) => (
                        <TableRow key={idx} className={!row.valid ? 'bg-rose-50' : row.insufficiencies?.length > 0 ? 'bg-amber-50' : ''}>
                          <TableCell>{row.row_number}</TableCell>
                          <TableCell className="font-mono text-xs">{row.employee_code || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              row.employee_category === 'fresher' ? 'bg-green-50 text-green-700 border-green-300' :
                              row.employee_category === 'intern' ? 'bg-purple-50 text-purple-700 border-purple-300' :
                              row.employee_category === 'trainee' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                              'bg-blue-50 text-blue-700 border-blue-300'
                            }>
                              {(row.employee_category || 'experienced').charAt(0).toUpperCase() + (row.employee_category || 'experienced').slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.email}</TableCell>
                          <TableCell>{row.phone}</TableCell>
                          <TableCell>
                            {!row.valid ? (
                              <Badge className="bg-rose-100 text-rose-700">Error</Badge>
                            ) : row.insufficiencies?.length > 0 ? (
                              <Badge className="bg-amber-100 text-amber-700">Insufficiency</Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700">Valid</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">
                            {row.errors?.join(', ') || row.insufficiencies?.join(', ') || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
          
          {bulkData.length > 0 && (
            <div className="border rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="sendBulkEmails"
                  checked={sendBulkEmails}
                  onCheckedChange={setSendBulkEmails}
                />
                <label htmlFor="sendBulkEmails" className="text-sm font-medium cursor-pointer">
                  Send invitation emails with login credentials
                </label>
              </div>
              <p className="text-xs text-slate-600 mt-2 ml-6">
                Each candidate will receive an email with:
                <br />• Default password: <strong>Welcome@2026</strong> (candidates must reset on first login)
                <br />• Instructions to change password on first login
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkImport(false); setBulkData([]); setBulkErrors([]); }}>Cancel</Button>
            <Button 
              onClick={handleBulkImport} 
              disabled={bulkData.filter(d => d.valid).length === 0}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import {bulkData.filter(d => d.valid).length} Candidates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Candidate Dialog - Enhanced with Documents */}
      <Dialog open={!!showViewEmployee} onOpenChange={(o) => { if (!o) { setShowViewEmployee(null); setEmployeeFullDetails(null); setActivityLog([]); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Candidate Details
            </DialogTitle>
          </DialogHeader>
          {showViewEmployee && (
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-7">
                <TabsTrigger value="basic" className="text-xs">Basic Info</TabsTrigger>
                <TabsTrigger value="address" className="text-xs">Address</TabsTrigger>
                <TabsTrigger value="employment" className="text-xs">Employment</TabsTrigger>
                <TabsTrigger value="education" className="text-xs">Education</TabsTrigger>
                <TabsTrigger value="documents" className="text-xs">Documents</TabsTrigger>
                <TabsTrigger value="case" className="text-xs">Case Info</TabsTrigger>
                <TabsTrigger value="activity" className="text-xs">Audit Trail</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Employee ID / Offer ID</span>
                    <p className="font-medium font-mono">{showViewEmployee.employee_code || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Employee Category</span>
                    <div className="mt-1">
                      <Badge variant="outline" className={
                        showViewEmployee.employee_category === 'fresher' ? 'bg-green-50 text-green-700 border-green-300' :
                        showViewEmployee.employee_category === 'intern' ? 'bg-purple-50 text-purple-700 border-purple-300' :
                        showViewEmployee.employee_category === 'trainee' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                        'bg-blue-50 text-blue-700 border-blue-300'
                      }>
                        {(showViewEmployee.employee_category || 'experienced').charAt(0).toUpperCase() + (showViewEmployee.employee_category || 'experienced').slice(1)}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Status</span>
                    <div className="mt-1">{getStatusBadge(showViewEmployee)}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Name</span>
                    <p className="font-medium">{showViewEmployee.name || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Father's Name</span>
                    <p className="font-medium">{showViewEmployee.father_name || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Date of Birth</span>
                    <p className="font-medium">{showViewEmployee.dob || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Gender</span>
                    <p className="font-medium">{showViewEmployee.gender || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Email</span>
                    <p className="font-medium">{showViewEmployee.email || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Phone</span>
                    <p className="font-medium">{showViewEmployee.phone || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Alternate Phone</span>
                    <p className="font-medium">{showViewEmployee.alternate_phone || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Entity</span>
                    <p className="font-medium">{getEntityName(showViewEmployee.entity_id)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs uppercase">Case ID</span>
                    <p className="font-medium font-mono text-xs">{showViewEmployee.case_id || '-'}</p>
                  </div>
                </div>
                
                {/* ID Details */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">ID Details</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 text-xs uppercase">PAN Number</span>
                      <p className="font-medium font-mono">{showViewEmployee.pan_number || '-'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 text-xs uppercase">Aadhar Number</span>
                      <p className="font-medium font-mono">{showViewEmployee.aadhar_number || '-'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 text-xs uppercase">UAN Number</span>
                      <p className="font-medium font-mono">{showViewEmployee.uan_number || '-'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 text-xs uppercase">Passport Number</span>
                      <p className="font-medium font-mono">{showViewEmployee.passport_number || '-'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 text-xs uppercase">Driving License</span>
                      <p className="font-medium font-mono">{showViewEmployee.driving_license || '-'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 text-xs uppercase">Voter ID</span>
                      <p className="font-medium font-mono">{showViewEmployee.voter_id || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* References Section */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">References</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Personal References */}
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 text-xs uppercase mb-2 block">Personal References</span>
                      {showViewEmployee.personal_references?.length > 0 ? (
                        <div className="space-y-2">
                          {showViewEmployee.personal_references.map((ref, idx) => (
                            <div key={idx} className="text-sm p-2 bg-white rounded border">
                              <p className="font-medium">{ref.name || '-'}</p>
                              <p className="text-slate-500 text-xs">{ref.relationship} • {ref.phone}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-sm">No personal references</p>
                      )}
                    </div>
                    {/* Professional References */}
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 text-xs uppercase mb-2 block">Professional References</span>
                      {showViewEmployee.professional_references?.length > 0 ? (
                        <div className="space-y-2">
                          {showViewEmployee.professional_references.map((ref, idx) => (
                            <div key={idx} className="text-sm p-2 bg-white rounded border">
                              <p className="font-medium">{ref.name || '-'}</p>
                              <p className="text-slate-500 text-xs">{ref.designation} @ {ref.company}</p>
                              <p className="text-slate-500 text-xs">{ref.phone} • {ref.email}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-sm">No professional references</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Auto-Filled Verification Data Preview */}
                <div className="pt-4 border-t">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Auto-Filled Verification Data Preview
                    </h4>
                    <p className="text-xs text-blue-700 mb-4">The following data is automatically copied for various verification checks</p>
                    
                    <div className="space-y-3">
                      {/* World Database Check */}
                      <div className="bg-white rounded p-3 border border-blue-100">
                        <p className="font-medium text-slate-800 text-sm flex items-center gap-2 mb-2">
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          World Database Check
                        </p>
                        <p className="text-xs text-slate-500 mb-2">Copies: Basic Details (Name, DOB, Father Name)</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Name:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.name || '-'}</span>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">DOB:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.dob || '-'}</span>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Father&apos;s Name:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.father_name || '-'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Credit Check */}
                      <div className="bg-white rounded p-3 border border-blue-100">
                        <p className="font-medium text-slate-800 text-sm flex items-center gap-2 mb-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          Credit Check
                        </p>
                        <p className="text-xs text-slate-500 mb-2">Copies: Basic Details + PAN Card</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Name:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.name || '-'}</span>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">DOB:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.dob || '-'}</span>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">PAN:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.pan_number || '-'}</span>
                          </div>
                        </div>
                      </div>

                      {/* ID Verification */}
                      <div className="bg-white rounded p-3 border border-blue-100">
                        <p className="font-medium text-slate-800 text-sm flex items-center gap-2 mb-2">
                          <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                          ID Verification (PAN / Aadhar)
                        </p>
                        <p className="text-xs text-slate-500 mb-2">Copies: Basic Details + PAN/Aadhar Number</p>
                        <div className="grid grid-cols-4 gap-2 text-xs">
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Name:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.name || '-'}</span>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">DOB:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.dob || '-'}</span>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">PAN:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.pan_number || '-'}</span>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Aadhar:</span>
                            <span className="ml-1 font-medium">{showViewEmployee.aadhar_number || '-'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Criminal Court Check */}
                      <div className="bg-white rounded p-3 border border-blue-100">
                        <p className="font-medium text-slate-800 text-sm flex items-center gap-2 mb-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                          Criminal Court / Criminal Advocate Check
                        </p>
                        <p className="text-xs text-slate-500 mb-2">Copies: Present Address + Subsequent Addresses</p>
                        <div className="space-y-2 text-xs">
                          {(showViewEmployee.present_house_no || showViewEmployee.present_city) && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">Present:</span>
                              <span className="ml-1 font-medium">
                                {[showViewEmployee.present_house_no, showViewEmployee.present_area, showViewEmployee.present_city, showViewEmployee.present_state, showViewEmployee.present_pincode].filter(Boolean).join(', ') || '-'}
                              </span>
                            </div>
                          )}
                          {(showViewEmployee.permanent_house_no || showViewEmployee.permanent_city) && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">Permanent:</span>
                              <span className="ml-1 font-medium">
                                {[showViewEmployee.permanent_house_no, showViewEmployee.permanent_area, showViewEmployee.permanent_city, showViewEmployee.permanent_state, showViewEmployee.permanent_pincode].filter(Boolean).join(', ') || '-'}
                              </span>
                            </div>
                          )}
                          {showViewEmployee.additional_addresses?.filter(a => a.house_no || a.area || a.city).map((addr, idx) => (
                            <div key={idx} className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">Additional {idx + 1} ({addr.address_type}):</span>
                              <span className="ml-1 font-medium">
                                {[addr.house_no, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || '-'}
                              </span>
                            </div>
                          ))}
                          {!showViewEmployee.present_house_no && !showViewEmployee.present_city && !showViewEmployee.permanent_house_no && !showViewEmployee.permanent_city && (
                            <div className="bg-amber-50 p-2 rounded text-amber-700">
                              No addresses available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="address" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Present Address</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-2">
                      {(showViewEmployee.present_address?.complete_address || showViewEmployee.present_address?.city || showViewEmployee.present_house_no || showViewEmployee.present_city) ? (
                        <>
                          {showViewEmployee.present_address?.complete_address && (
                            <p><span className="text-slate-500">Address:</span> {showViewEmployee.present_address.complete_address}</p>
                          )}
                          <p><span className="text-slate-500">House/Building:</span> {showViewEmployee.present_address?.house_no || showViewEmployee.present_house_no || '-'}</p>
                          <p><span className="text-slate-500">Area:</span> {showViewEmployee.present_address?.area || showViewEmployee.present_area || '-'}</p>
                          <p><span className="text-slate-500">City:</span> {showViewEmployee.present_address?.city || showViewEmployee.present_city || '-'}</p>
                          <p><span className="text-slate-500">State:</span> {showViewEmployee.present_address?.state || showViewEmployee.present_state || '-'}</p>
                          <p><span className="text-slate-500">Pincode:</span> {showViewEmployee.present_address?.pincode || showViewEmployee.present_pincode || '-'}</p>
                          {(showViewEmployee.present_address?.residence_type || showViewEmployee.present_residence_type) && (
                            <p><span className="text-slate-500">Residence Type:</span> {showViewEmployee.present_address?.residence_type || showViewEmployee.present_residence_type}</p>
                          )}
                          {(showViewEmployee.present_address?.period_of_stay || showViewEmployee.present_period_of_stay) && (
                            <p><span className="text-slate-500">Period of Stay:</span> {showViewEmployee.present_address?.period_of_stay || showViewEmployee.present_period_of_stay}</p>
                          )}
                        </>
                      ) : (
                        <p className="text-slate-400 italic">No address provided</p>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Permanent Address</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-2">
                      {(showViewEmployee.permanent_address?.complete_address || showViewEmployee.permanent_address?.city || showViewEmployee.permanent_house_no || showViewEmployee.permanent_city) ? (
                        <>
                          {showViewEmployee.permanent_address?.complete_address && (
                            <p><span className="text-slate-500">Address:</span> {showViewEmployee.permanent_address.complete_address}</p>
                          )}
                          <p><span className="text-slate-500">House/Building:</span> {showViewEmployee.permanent_address?.house_no || showViewEmployee.permanent_house_no || '-'}</p>
                          <p><span className="text-slate-500">Area:</span> {showViewEmployee.permanent_address?.area || showViewEmployee.permanent_area || '-'}</p>
                          <p><span className="text-slate-500">City:</span> {showViewEmployee.permanent_address?.city || showViewEmployee.permanent_city || '-'}</p>
                          <p><span className="text-slate-500">State:</span> {showViewEmployee.permanent_address?.state || showViewEmployee.permanent_state || '-'}</p>
                          <p><span className="text-slate-500">Pincode:</span> {showViewEmployee.permanent_address?.pincode || showViewEmployee.permanent_pincode || '-'}</p>
                        </>
                      ) : (
                        <p className="text-slate-400 italic">No address provided</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              {/* Employment Tab */}
              <TabsContent value="employment" className="mt-4">
                {showViewEmployee.employee_category && showViewEmployee.employee_category !== 'experienced' ? (
                  <div className="p-6 bg-slate-50 rounded-lg text-center">
                    <Badge variant="outline" className={
                      showViewEmployee.employee_category === 'fresher' ? 'bg-green-50 text-green-700 border-green-300' :
                      showViewEmployee.employee_category === 'intern' ? 'bg-purple-50 text-purple-700 border-purple-300' :
                      'bg-orange-50 text-orange-700 border-orange-300'
                    }>
                      {showViewEmployee.employee_category.charAt(0).toUpperCase() + showViewEmployee.employee_category.slice(1)} - Employment Not Required
                    </Badge>
                    <p className="text-slate-500 text-sm mt-2">Employment verification is skipped for this category</p>
                  </div>
                ) : showViewEmployee.employment_entries?.length > 0 ? (
                  <div className="space-y-4">
                    {showViewEmployee.employment_entries.map((emp, idx) => (
                      <Card key={idx} className="border-slate-200">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-base">{emp.company_name || 'Employer ' + (idx + 1)}</CardTitle>
                            <Badge variant="outline" className={emp.employment_type === 'current' || emp.is_current ? 'bg-green-50 text-green-700' : 'bg-slate-100'}>
                              {emp.employment_type === 'current' || emp.is_current ? 'Current' : 'Past'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="text-sm">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                              <span className="text-slate-500 text-xs uppercase">Designation</span>
                              <p className="font-medium">{emp.designation || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">Employee ID</span>
                              <p className="font-medium font-mono">{emp.employee_id || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">Period</span>
                              <p className="font-medium">{emp.employed_from || '-'} to {emp.employed_to || 'Present'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">CTC / Salary</span>
                              <p className="font-medium">{emp.salary_per_month || emp.ctc || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">UAN / PF Number</span>
                              <p className="font-medium font-mono">{emp.uan_pf_number || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">Location</span>
                              <p className="font-medium">{emp.location_address || emp.company_location || '-'}</p>
                            </div>
                            {(emp.rm_name || emp.reporting_manager_name) && (
                              <div className="col-span-2">
                                <span className="text-slate-500 text-xs uppercase">Reporting Manager</span>
                                <p className="font-medium">
                                  {emp.rm_name || emp.reporting_manager_name} 
                                  {(emp.rm_email || emp.reporting_manager_email) ? ` (${emp.rm_email || emp.reporting_manager_email})` : ''}
                                  {emp.rm_contact_number ? ` - ${emp.rm_contact_number}` : ''}
                                </p>
                              </div>
                            )}
                            {(emp.hr_name || emp.hr_contact_name) && (
                              <div className="col-span-2">
                                <span className="text-slate-500 text-xs uppercase">HR Contact</span>
                                <p className="font-medium">
                                  {emp.hr_name || emp.hr_contact_name} 
                                  {(emp.hr_email || emp.hr_contact_email) ? ` (${emp.hr_email || emp.hr_contact_email})` : ''}
                                  {emp.hr_contact_number ? ` - ${emp.hr_contact_number}` : ''}
                                </p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 bg-slate-50 rounded-lg text-center text-slate-500">
                    No employment history entered
                  </div>
                )}
              </TabsContent>
              
              {/* Education Tab */}
              <TabsContent value="education" className="mt-4">
                {showViewEmployee.education_entries?.length > 0 ? (
                  <div className="space-y-4">
                    {showViewEmployee.education_entries.map((edu, idx) => (
                      <Card key={idx} className="border-slate-200">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-base">{edu.course || edu.degree || edu.qualification || 'Education ' + (idx + 1)}</CardTitle>
                            <Badge variant="outline" className={edu.education_type === 'highest' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}>
                              {edu.education_type === 'highest' ? 'Highest' : 'Prior'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="text-sm">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                              <span className="text-slate-500 text-xs uppercase">Name (as per document)</span>
                              <p className="font-medium">{edu.name_as_per_document || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">Institution / College</span>
                              <p className="font-medium">{edu.college || edu.institution_name || edu.college_name || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">University / Board</span>
                              <p className="font-medium">{edu.university_name || edu.board || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">Specialization</span>
                              <p className="font-medium">{edu.specialization || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">Year of Passing</span>
                              <p className="font-medium">{edu.year_of_passing || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs uppercase">Roll Number</span>
                              <p className="font-medium font-mono">{edu.roll_no || edu.roll_number || '-'}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 bg-slate-50 rounded-lg text-center text-slate-500">
                    No education history entered
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="documents" className="mt-4">
                {loadingDetails ? (
                  <div className="text-center py-8 text-slate-500">Loading documents...</div>
                ) : employeeFullDetails?.documents?.length > 0 ? (
                  <div className="space-y-3">
                    {employeeFullDetails.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="font-medium text-sm">{doc.document_type?.replace(/_/g, ' ').toUpperCase()}</p>
                            <p className="text-xs text-slate-500">{doc.file_name}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {doc.file_url && (
                            <Button variant="outline" size="sm" onClick={() => window.open(doc.file_url, '_blank')}>
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          )}
                          {doc.file_url && (
                            <Button variant="outline" size="sm" onClick={() => {
                              const a = document.createElement('a');
                              a.href = doc.file_url;
                              a.download = doc.file_name;
                              a.click();
                            }}>
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No documents uploaded yet</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="case" className="mt-4">
                {loadingDetails ? (
                  <div className="text-center py-8 text-slate-500">Loading case info...</div>
                ) : employeeFullDetails?.case ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-500 text-xs uppercase">Case ID</span>
                        <p className="font-medium font-mono">{employeeFullDetails.case.id}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-500 text-xs uppercase">Status</span>
                        <Badge className={
                          employeeFullDetails.case.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          employeeFullDetails.case.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {employeeFullDetails.case.status?.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-500 text-xs uppercase">Created</span>
                        <p className="font-medium">{new Date(employeeFullDetails.case.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-500 text-xs uppercase">Checks</span>
                        <p className="font-medium">{employeeFullDetails.case.checks?.length || 0} checks</p>
                      </div>
                    </div>
                    
                    {/* Checks List */}
                    {employeeFullDetails.case.checks?.length > 0 && (
                      <div className="pt-4 border-t">
                        <h4 className="font-medium mb-3">Verification Checks</h4>
                        <div className="space-y-2">
                          {employeeFullDetails.case.checks.map((check, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 border rounded text-sm">
                              <span>{getCheckDisplayName(check.check_type)}</span>
                              <Badge className={
                                check.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                                check.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                check.status === 'failed' ? 'bg-rose-100 text-rose-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {check.status?.toUpperCase()}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Insufficiencies */}
                    {employeeFullDetails.insufficiencies?.length > 0 && (
                      <div className="pt-4 border-t">
                        <h4 className="font-medium mb-3 text-amber-700">
                          <AlertTriangle className="w-4 h-4 inline mr-2" />
                          Insufficiencies ({employeeFullDetails.insufficiencies.length})
                        </h4>
                        <div className="space-y-2">
                          {employeeFullDetails.insufficiencies.map((insuff, idx) => (
                            <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium">{insuff.reason}</p>
                                  <p className="text-slate-600 text-xs mt-1">{insuff.description}</p>
                                </div>
                                <Badge className={
                                  insuff.status === 'closed' ? 'bg-emerald-100 text-emerald-700' :
                                  insuff.status === 'waiting_for_clearance' ? 'bg-blue-100 text-blue-700' :
                                  'bg-amber-100 text-amber-700'
                                }>
                                  {insuff.status?.replace(/_/g, ' ').toUpperCase()}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Report Download Buttons */}
                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-3">Download Reports</h4>
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          onClick={() => generateReport(employeeFullDetails.case.id, 'interim')}
                          className="flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          Interim Report
                        </Button>
                        <Button 
                          className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
                          onClick={() => generateReport(employeeFullDetails.case.id, 'final')}
                          disabled={!['completed', 'final_report_qc', 'report_approved'].includes(employeeFullDetails.case.status)}
                        >
                          <Download className="w-4 h-4" />
                          Final Report
                        </Button>
                      </div>
                      {!['completed', 'final_report_qc', 'report_approved'].includes(employeeFullDetails.case.status) && (
                        <p className="text-xs text-slate-500 mt-2">Final report will be available once verification is completed.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No case created yet</p>
                  </div>
                )}
              </TabsContent>

              {/* Activity Log Tab */}
              <TabsContent value="activity" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    {employeeFullDetails?.case ? (
                      <AuditTrail caseId={employeeFullDetails.case.id || employeeFullDetails.case.case_id} />
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        No case created yet - Audit trail will appear once verification case is initiated
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Candidate Dialog (with approval workflow) */}
      <Dialog open={!!showEditEmployee} onOpenChange={(o) => { if (!o) setShowEditEmployee(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Edit Candidate
              <Badge className="bg-amber-100 text-amber-700">Requires Admin Approval</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {showEditEmployee && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Changes to submitted data require admin approval before being applied.
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Emp ID / Offer ID</Label>
                  <Input 
                    value={editForm.employee_code || ''} 
                    onChange={(e) => setEditForm({...editForm, employee_code: e.target.value})}
                    placeholder="Employee or Offer ID"
                  />
                </div>
                <div>
                  <Label>Employee Category</Label>
                  <Select 
                    value={editForm.employee_category || 'experienced'} 
                    onValueChange={(v) => setEditForm({...editForm, employee_category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="experienced">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">Experienced</Badge>
                        </div>
                      </SelectItem>
                      <SelectItem value="fresher">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Fresher</Badge>
                        </div>
                      </SelectItem>
                      <SelectItem value="intern">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">Intern</Badge>
                        </div>
                      </SelectItem>
                      <SelectItem value="trainee">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">Trainee</Badge>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Name</Label>
                  <Input value={editForm.name || ''} onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
                </div>
                <div>
                  <Label>Father's Name</Label>
                  <Input value={editForm.father_name || ''} onChange={(e) => setEditForm({...editForm, father_name: e.target.value})} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={editForm.email || ''} onChange={(e) => setEditForm({...editForm, email: e.target.value})} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={editForm.phone || ''} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} />
                </div>
                <div>
                  <Label>DOB</Label>
                  <Input type="date" value={editForm.dob || ''} onChange={(e) => setEditForm({...editForm, dob: e.target.value})} />
                </div>
              </div>
              
              <div>
                <Label>Reason for Edit *</Label>
                <Textarea
                  placeholder="Please provide a reason for this edit..."
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditEmployee(null)}>Cancel</Button>
            <Button onClick={handleSubmitEdit} className="bg-slate-900 hover:bg-slate-800">
              Submit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stop Check Dialog */}
      <Dialog open={!!showStopCheck} onOpenChange={(o) => { if (!o) { setShowStopCheck(null); setStopCheckReason(''); setStopCheckDescription(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <StopCircle className="w-5 h-5" />
              Stop Verification Check
            </DialogTitle>
          </DialogHeader>
          
          {showStopCheck && (
            <div className="space-y-4">
              <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                This action will stop all in-progress verification checks for <strong>{showStopCheck.name}</strong>.
                This action cannot be undone.
              </div>
              
              <div>
                <Label>Employee</Label>
                <p className="text-sm font-medium text-slate-900">{showStopCheck.name}</p>
                <p className="text-xs text-slate-500">{showStopCheck.email}</p>
              </div>
              
              <div>
                <Label>Reason for Stopping *</Label>
                <Select value={stopCheckReason} onValueChange={setStopCheckReason}>
                  <SelectTrigger data-testid="stop-check-reason-select">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="candidate_no_show">Candidate No Show</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                    <SelectItem value="candidate_withdrew">Candidate Withdrew</SelectItem>
                    <SelectItem value="offer_revoked">Offer Revoked</SelectItem>
                    <SelectItem value="other">Other (Specify)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {stopCheckReason === 'other' && (
                <div>
                  <Label>Please Specify</Label>
                  <Textarea
                    placeholder="Provide details for stopping the check..."
                    value={stopCheckDescription}
                    onChange={(e) => setStopCheckDescription(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
              
              {stopCheckReason && stopCheckReason !== 'other' && (
                <div>
                  <Label>Additional Notes (Optional)</Label>
                  <Textarea
                    placeholder="Any additional details..."
                    value={stopCheckDescription}
                    onChange={(e) => setStopCheckDescription(e.target.value)}
                    rows={2}
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowStopCheck(null); setStopCheckReason(''); setStopCheckDescription(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleStopCheck} 
              disabled={stoppingCheck || !stopCheckReason || (stopCheckReason === 'other' && !stopCheckDescription)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-stop-check-btn"
            >
              {stoppingCheck ? 'Stopping...' : 'Stop Verification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Package Dialog */}
      <Dialog open={!!showChangePackage} onOpenChange={(o) => { if (!o) { setShowChangePackage(null); setSelectedPackageId(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Package className="w-5 h-5" />
              Change Verification Package
            </DialogTitle>
          </DialogHeader>
          
          {showChangePackage && (
            <div className="space-y-4">
              <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Changing the package will update all verification checks for <strong>{showChangePackage.name}</strong>.
                Any existing check data will be replaced with the new package checks.
              </div>
              
              <div>
                <Label>Employee</Label>
                <p className="text-sm font-medium text-slate-900">{showChangePackage.name}</p>
                <p className="text-xs text-slate-500">{showChangePackage.email}</p>
              </div>
              
              <div>
                <Label>Select New Package *</Label>
                <Select value={selectedPackageId} onValueChange={setSelectedPackageId}>
                  <SelectTrigger data-testid="change-package-select">
                    <SelectValue placeholder="Select a package" />
                  </SelectTrigger>
                  <SelectContent>
                    {(filteredPackages.length > 0 ? filteredPackages : (user?.role === 'hr_admin' ? [] : packages)).map(pkg => {
                      // Calculate total checks including quantities
                      const totalChecks = pkg.checks?.reduce((sum, c) => sum + (c.quantity || 1), 0) || 0;
                      return (
                        <SelectItem key={pkg.id} value={pkg.id}>
                          {pkg.name} ({totalChecks} checks) - ₹{pkg.price}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {clientInfo?.associated_packages?.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1">
                    Showing packages assigned to your company
                  </p>
                )}
              </div>
              
              {selectedPackageId && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="text-xs text-slate-500">Package Checks</Label>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(() => {
                      const checks = packages.find(p => p.id === selectedPackageId)?.checks || [];
                      // Use quantity from package definition
                      return checks.map((check, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {getCheckDisplayName(check.check_type)} ({check.quantity || 1})
                        </Badge>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowChangePackage(null); setSelectedPackageId(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangePackage} 
              disabled={changingPackage || !selectedPackageId}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-change-package-btn"
            >
              {changingPackage ? 'Changing...' : 'Change Package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Basic Info Edit Request Review Dialog */}
      <Dialog open={!!showBasicEditReview} onOpenChange={(o) => { if (!o) { setShowBasicEditReview(null); setReviewNotes(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Edit className="w-5 h-5" />
              Review Basic Info Edit Request
            </DialogTitle>
          </DialogHeader>
          
          {showBasicEditReview && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="font-semibold text-blue-800">{showBasicEditReview.employee_name}</p>
                <p className="text-sm text-blue-600">{showBasicEditReview.employee_email}</p>
              </div>
              
              <div>
                <Label className="text-sm font-semibold">Fields Requested to Edit</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {showBasicEditReview.fields_to_edit?.map((field, idx) => (
                    <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                      {field.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-semibold">Reason</Label>
                <p className="text-sm text-slate-700 p-2 bg-slate-50 rounded border mt-1">
                  {showBasicEditReview.reason}
                </p>
              </div>

              <div>
                <Label className="text-sm font-semibold">Review Notes (Optional)</Label>
                <Textarea
                  placeholder="Add any notes for the employee..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="mt-1"
                  data-testid="review-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => handleReviewBasicEditRequest(showBasicEditReview?.id, 'reject')}
              data-testid="reject-basic-edit-btn"
            >
              Reject
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleReviewBasicEditRequest(showBasicEditReview?.id, 'approve')}
              data-testid="approve-basic-edit-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extracted Resume Data Preview Dialog */}
      <Dialog open={showExtractedPreview} onOpenChange={setShowExtractedPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              Extracted Resume Data
            </DialogTitle>
          </DialogHeader>
          {extractedData && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                The following data was extracted from the resume. Click "Apply to Form" to auto-fill the candidate form.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {extractedData.name && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Name:</span>
                    <span className="text-gray-900">{extractedData.name}</span>
                  </div>
                )}
                {extractedData.email && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Email:</span>
                    <span className="text-gray-900">{extractedData.email}</span>
                  </div>
                )}
                {extractedData.phone && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Phone:</span>
                    <span className="text-gray-900">{extractedData.phone}</span>
                  </div>
                )}
                {(extractedData.date_of_birth || extractedData.dob) && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Date of Birth:</span>
                    <span className="text-gray-900">{extractedData.date_of_birth || extractedData.dob}</span>
                  </div>
                )}
                {extractedData.father_name && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Father's Name:</span>
                    <span className="text-gray-900">{extractedData.father_name}</span>
                  </div>
                )}
                {(extractedData.present_address || extractedData.address) && (
                  <div>
                    <span className="font-medium text-gray-700">Address:</span>
                    <p className="text-gray-900 mt-1">
                      {typeof (extractedData.present_address || extractedData.address) === 'object' 
                        ? Object.values(extractedData.present_address || extractedData.address).filter(Boolean).join(', ')
                        : (extractedData.present_address || extractedData.address)
                      }
                    </p>
                  </div>
                )}
                {extractedData.education && Array.isArray(extractedData.education) && extractedData.education.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Education:</span>
                    <ul className="mt-1 list-disc list-inside text-gray-900">
                      {extractedData.education.map((edu, idx) => (
                        <li key={idx}>
                          {edu.degree || edu.qualification} - {edu.institution || edu.university} ({edu.year || edu.passing_year})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {extractedData.employment && Array.isArray(extractedData.employment) && extractedData.employment.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Employment History:</span>
                    <ul className="mt-1 list-disc list-inside text-gray-900">
                      {extractedData.employment.slice(0, 3).map((emp, idx) => (
                        <li key={idx}>
                          {emp.designation || emp.role} at {emp.company || emp.organization} ({emp.duration || `${emp.from} - ${emp.to}`})
                        </li>
                      ))}
                      {extractedData.employment.length > 3 && (
                        <li className="text-gray-500">+{extractedData.employment.length - 3} more entries</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowExtractedPreview(false)}>
                  Cancel
                </Button>
                <Button onClick={applyExtractedData} className="bg-violet-600 hover:bg-violet-700">
                  <Check className="h-4 w-4 mr-2" />
                  Apply to Form
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
